import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Eraser, X, ArrowDown, Download, Check, AlertCircle } from 'lucide-react';
import { sendMessage, getSessionHistory } from '../api/chatbotApi';
import { getRoleConfig } from '../config/roleConfig';
import {
  downloadReport,
  fetchBgvCompletedCandidates,
  fetchTotalCandidates,
  fetchFullCandidateReport,
} from '../../services/user/api';
import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import ScanLineLoader from './ScanLineLoader';
import CapabilityChips from './CapabilityChips';
import logo from '../../assets/images/Trueface.png';
import '../chatbot.css';

const SESSION_KEY = 'bgc_chat_session_id';
const SESSION_OWNER_KEY = 'bgc_chat_session_owner';
const SESSION_MESSAGES_KEY = 'bgc_chat_messages';
const SESSION_LAST_ACTIVE_KEY = 'bgc_chat_last_active';
const SESSION_REFRESH_TOKEN_KEY = 'bgc_chat_refresh_token';

const isSessionValid = (ownerEmail) => {
  const storedId = localStorage.getItem(SESSION_KEY);
  const storedOwner = localStorage.getItem(SESSION_OWNER_KEY);
  const storedToken = localStorage.getItem(SESSION_REFRESH_TOKEN_KEY);
  const currentToken = localStorage.getItem('bgc_refresh_token') || localStorage.getItem('bgc_token');

  if (!storedId || !ownerEmail) return false;
  // If a new login occurred (new auth/refresh token), start a fresh chat
  if (storedToken && currentToken && storedToken !== currentToken) return false;
  return storedOwner === ownerEmail;
};

const getInitialMessages = (role, username) => {
  const normRole = (role || '').toUpperCase();
  const displayName = username || 'User';

  let roleTitle = 'Client';
  let capabilities = [
    `- **Live Candidate Status & Tracking**: Check live verification progress and drill down into component checks (*Education*, *Employment*, *Address*, *Criminal*, *Identity*).`,
    `- **Dashboard & Pipeline Metrics**: Instant counts for *Total Candidates*, *Pending Submissions*, *WIP*, and *BGC Completed*.`,
    `- **Navigation & Process Guidance**: Step-by-step guidance on registering candidates, tracking form submissions, and requesting BGC.`,
    `- **Credit Policy & Billing Assistance**: Package pricing, credit validation, and transaction dispute guidance.`,
    `- **Support Escalations**: Generate priority support tickets (\`TKT-BGC-...\`) dispatched directly to TrueFace Administration.`,
  ];

  if (normRole === 'ADMIN' || normRole === 'SUPERADMIN') {
    roleTitle = 'Admin';
    capabilities = [
      `- **System-Wide Operational KPIs**: Global counts of registered clients, active verification agents, and total platform candidates.`,
      `- **Client Organization Approvals**: Review and audit pending, approved, and rejected client registrations.`,
      `- **Employee Lifecycle & Capacity**: Verification agent onboarding, active caseload tracking, and account deactivations.`,
      `- **Workload Allocation & Reassignment**: Monitor agent turnaround times and reallocate stalled files to active agents.`,
      `- **Audit & Verification Reports**: Authorize and audit final Result of Verification reports before release to clients.`,
    ];
  } else if (normRole === 'EMPLOYEE' || normRole === 'AGENT' || normRole === 'VERIFIER') {
    roleTitle = 'Employee';
    capabilities = [
      `- **Assigned Queue & Workload**: View and filter candidates assigned to you across *Work In Progress*, *Completed*, and *Verified* stages.`,
      `- **Candidate Verification Dossier**: Inquire about any candidate by ID (e.g., \`TRU000655\`) to view real-time check statuses, documents, and findings.`,
      `- **Check Execution Protocols**: Detailed verification requirements for *Personal*, *Address*, *Education*, *Employment*, *UAN*, and *Criminal* checks.`,
      `- **Verification Methodology & AI Modules**: Protocols for Virtual/Physical site checks, Document Forgery Detection, Face Matching, and Payslip analysis.`,
      `- **Findings & Remarks Standards**: Evidence upload standards (max 5MB) and mandatory objective verifier remarks.`,
      `- **Profile Management**: View employee code, operational department, and update your registered contact details.`,
    ];
  } else if (normRole === 'CANDIDATE' || normRole === 'APPLICANT') {
    roleTitle = 'Candidate';
    capabilities = [
      `- **5-Stage Application Guidance**: Step-by-step assistance through Personal, Address, Education, Employment, and Declaration stages.`,
      `- **Document Specifications**: Upload standards (max 5MB, PDF/PNG/JPG) and required document checklists.`,
      `- **DigiLocker Integration**: Instant identity and permanent address retrieval via Aadhaar OTP.`,
      `- **Digital Signatures**: Guidance for Draw, Type, and Upload signature methods.`,
      `- **Application Status & Edits**: Check submission status and learn how to submit a **Request to Edit** if corrections are needed after lock.`,
    ];
  }

  const welcomeContent = `### 👋 Welcome, ${displayName}!\nI am your **TrueFace AI Assistant for the ${roleTitle}**.\n\nHere is what you can ask me to do:\n\n${capabilities.join('\n')}`;

  return [
    { role: 'assistant', content: welcomeContent, isWelcome: true },
  ];
};

/**
 * FloatingChatWidget — Production chatbot widget embedded inside BGCM-UI.
 *
 * Session is read directly from the same localStorage that Login.js writes:
 *   bgc_token   → JWT sent as Bearer on every request
 *   user_role   → drives RBAC colour theme and example prompts
 *   user_name   → shown in the user badge
 *   user_email  → used to scope the session key per user
 *
 * No props required — everything is self-contained.
 */
const FloatingChatWidget = () => {
  // ── Read session from localStorage (same app = same origin) ────────────────
  const pathname = typeof window !== 'undefined' && window.location ? window.location.pathname : '';
  let activeRole = localStorage.getItem('user_role');
  if (pathname.startsWith('/admin')) {
    activeRole = 'ADMIN';
  } else if (pathname.startsWith('/client')) {
    activeRole = 'CLIENT';
  } else if (pathname.startsWith('/employee')) {
    activeRole = 'EMPLOYEE';
  } else if (pathname.startsWith('/candidate')) {
    activeRole = 'CANDIDATE';
  }
  const role     = activeRole;
  const username = localStorage.getItem('user_name') || localStorage.getItem('user_email') || 'User';
  const email    = localStorage.getItem('user_email') || '';

  const roleConfig = getRoleConfig(role);

  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState(() => {
    if (isSessionValid(email)) {
      try {
        const cached = localStorage.getItem(SESSION_MESSAGES_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const initial = getInitialMessages(role, username);
            const sanitized = parsed.map((m, idx) => {
              if (idx === 0 && (m.isWelcome || m.role === 'assistant')) {
                return { ...m, content: initial[0].content, isWelcome: true };
              }
              return m;
            });
            const hasUser = sanitized.some((m) => m.role === 'user');
            if (!hasUser) {
              return initial;
            }
            return sanitized;
          }
        }
      } catch (_) {}
    }
    return getInitialMessages(role, username);
  });
  const [sessionId, setSessionId] = useState(() => {
    return isSessionValid(email) ? localStorage.getItem(SESSION_KEY) : null;
  });
  const [loading, setLoading]   = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError]       = useState('');
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  // ── Client Module: Candidate Report Download State ─────────────────────────
  const normRole = (role || '').toUpperCase();
  const isClient =
    ['USER', 'CLIENT', 'HR', 'COMPANY'].includes(normRole) ||
    (typeof window !== 'undefined' && window.location.pathname.startsWith('/client'));

  const [activeCandidate, setActiveCandidate] = useState(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadQuery, setDownloadQuery] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);

  // ── Track candidate ID/name & verification completion from conversation ────
  useEffect(() => {
    if (!isClient || !Array.isArray(messages)) return;

    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (!msg?.content) continue;

      // Extract candidate details from assistant responses
      if (msg.role === 'assistant') {
        const idMatch = msg.content.match(/(?:Background Verification Dossier:?\s*`?|Candidate ID:?\s*`?|`)(TRU\d+)/i);
        const nameMatch =
          msg.content.match(/-\s*\*\*Candidate Name\*\*:\s*\*\*([^*\n]+)\*\*/i) ||
          msg.content.match(/(?:Name|Full Name):\s*\*\*([^*\n]+)\*\*/i);
        const statusMatch =
          msg.content.match(/-\s*\*\*Overall BGC Status\*\*:\s*\*\*([A-Za-z_ ]+)\*\*/i) ||
          msg.content.match(/(?:BGC Status|Status):\s*\*\*([A-Za-z_ ]+)\*\*/i);

        if (idMatch) {
          const cid = idMatch[1].toUpperCase();
          const cname = nameMatch ? nameMatch[1].trim() : '';
          const rawStatus = statusMatch ? statusMatch[1].trim().toUpperCase() : '';
          const isCompleted = ['COMPLETED', 'VERIFIED', 'CLEAR', 'APPROVED', 'PASSED'].some((s) =>
            rawStatus.includes(s)
          );

          setActiveCandidate({
            id: cid,
            name: cname,
            status: rawStatus || (isCompleted ? 'COMPLETED' : 'UNKNOWN'),
            isCompleted,
          });
          return;
        }
      }

      // Check user messages for candidate IDs
      if (msg.role === 'user') {
        const userMatch = msg.content.match(/\b(TRU\d+)\b/i);
        if (userMatch) {
          const cid = userMatch[1].toUpperCase();
          setActiveCandidate((prev) => {
            if (prev && prev.id === cid) return prev;
            return {
              id: cid,
              name: '',
              status: 'PENDING_STATUS',
              isCompleted: null,
            };
          });
          return;
        }
      }
    }
  }, [messages, isClient]);

  // ── Execute PDF report download via user API or Final Report ──────────────
  const executeReportDownload = async (candidateId, candidateName = '') => {
    try {
      setDownloadLoading(true);
      setDownloadStatus({
        type: 'info',
        message: `Preparing BGC report for ${candidateName || candidateId}...`,
      });

      // 1. If the Final Report container is already rendered on the page, click its Download button directly!
      const reportElement = document.getElementById('final-report-container');
      const directDownloadBtn = document.querySelector('.report-action-button');
      if (reportElement && directDownloadBtn) {
        directDownloadBtn.click();
        setDownloadStatus({
          type: 'success',
          message: `Generating and downloading PDF for ${candidateName || candidateId}...`,
        });
        return true;
      }

      // 2. Validate that the candidate's verification report is accessible
      try {
        const verifyRes = await fetchFullCandidateReport(candidateId);
        if (!verifyRes?.data?.status) {
          throw new Error('Report data unavailable');
        }
      } catch (checkErr) {
        setDownloadStatus({
          type: 'error',
          message: `Verification report for ${candidateName || candidateId} is not ready or has not been authorized yet.`,
        });
        return false;
      }

      // 3. Try direct backend blob download if available
      try {
        const response = await downloadReport(candidateId);
        const rawBlob = response instanceof Blob ? response : (response?.data || response);
        if (rawBlob && (rawBlob.size > 200 || rawBlob.type === 'application/pdf')) {
          const file = new Blob([rawBlob], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = fileURL;
          const safeName = candidateName ? candidateName.replace(/[^a-zA-Z0-9_-]/g, '_') : candidateId;
          link.setAttribute('download', `${safeName}_Report.pdf`);
          document.body.appendChild(link);
          link.click();
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(fileURL);
          }, 300);

          setDownloadStatus({
            type: 'success',
            message: `Report for ${candidateName || candidateId} downloaded successfully!`,
          });
          return true;
        }
      } catch (_) {
        // Blob API is not returning PDF directly, proceed to seamless official report viewer
      }

      // 4. Seamlessly route to the official TrueFace BGC Report with autodownload
      setDownloadStatus({
        type: 'success',
        message: `Opening official BGC Report for ${candidateName || candidateId} to generate and download PDF...`,
      });

      setTimeout(() => {
        window.location.href = `/client/finalreport/${candidateId}?autodownload=true`;
      }, 500);

      return true;
    } catch (err) {
      console.error('Error downloading report PDF:', err);
      setDownloadStatus({
        type: 'error',
        message: `Could not download PDF for ${candidateId}. Please ensure the report has been generated.`,
      });
      return false;
    } finally {
      setDownloadLoading(false);
    }
  };

  // ── Lookup candidate by ID or Name and download if verification completed ─
  const handleDownloadSubmit = async (queryInput) => {
    const raw = (queryInput !== undefined ? queryInput : downloadQuery).trim();
    if (!raw) {
      setDownloadStatus({
        type: 'error',
        message: 'Please enter a Candidate ID or Name.',
      });
      return;
    }

    setDownloadLoading(true);
    setDownloadStatus({
      type: 'info',
      message: `Searching records for "${raw}"...`,
    });

    try {
      const isId = /^TRU\d+$/i.test(raw) || /^\d+$/.test(raw);
      const formattedId = /^\d+$/.test(raw) ? `TRU${raw.padStart(6, '0')}` : raw.toUpperCase();

      // Step 1: Check BGV Completed candidates list
      const completedRes = await fetchBgvCompletedCandidates(0, 20, raw);
      const completedList = completedRes?.data?.data?.content || [];

      let matched = completedList.find((c) => {
        const cid = (c.candidateid || '').toUpperCase();
        const cname = (c.fullname || '').toLowerCase();
        return cid === formattedId || cname === raw.toLowerCase();
      });

      if (!matched && completedList.length > 0) {
        matched = completedList.find((c) =>
          (c.fullname || '').toLowerCase().includes(raw.toLowerCase())
        );
      }

      if (matched) {
        setActiveCandidate({
          id: matched.candidateid,
          name: matched.fullname,
          status: 'COMPLETED',
          isCompleted: true,
        });
        await executeReportDownload(matched.candidateid, matched.fullname);
        return;
      }

      // Step 2: Check Total candidates to verify if candidate exists but verification is NOT completed
      const totalRes = await fetchTotalCandidates(0, 20, raw);
      const totalList = totalRes?.data?.data?.content || [];

      let anyMatch = totalList.find((c) => {
        const cid = (c.candidateid || '').toUpperCase();
        const cname = (c.fullname || '').toLowerCase();
        return cid === formattedId || cname === raw.toLowerCase();
      });

      if (!anyMatch && totalList.length > 0) {
        anyMatch = totalList.find((c) =>
          (c.fullname || '').toLowerCase().includes(raw.toLowerCase())
        );
      }

      if (anyMatch) {
        const cStatus = (anyMatch.bgcStatus || anyMatch.status || '').toUpperCase();
        const isVerDone = ['COMPLETED', 'VERIFIED', 'CLEAR'].some((s) => cStatus.includes(s));

        if (isVerDone) {
          setActiveCandidate({
            id: anyMatch.candidateid,
            name: anyMatch.fullname,
            status: cStatus,
            isCompleted: true,
          });
          await executeReportDownload(anyMatch.candidateid, anyMatch.fullname);
        } else {
          setActiveCandidate({
            id: anyMatch.candidateid,
            name: anyMatch.fullname,
            status: cStatus || 'IN PROGRESS',
            isCompleted: false,
          });
          setDownloadStatus({
            type: 'error',
            message: `Verification for ${anyMatch.fullname || anyMatch.candidateid} is still ${cStatus || 'IN PROGRESS'}. PDF reports are only available for candidates whose verification is COMPLETED.`,
          });
        }
        return;
      }

      // Step 3: Direct candidate dossier lookup if ID was entered
      if (isId) {
        try {
          const reportRes = await fetchFullCandidateReport(formattedId);
          const reportData = reportRes?.data?.data;
          if (reportData) {
            const adminRes = reportData?.adminVerificationResult || reportData?.employeeVerificationInfo?.adminVerificationResult;
            const vStatus = (adminRes?.verificationStatus || reportData?.status || '').toUpperCase();
            const candName = reportData?.personalCheck
              ? `${reportData.personalCheck.firstName || ''} ${reportData.personalCheck.lastName || ''}`.trim()
              : formattedId;

            const isDone = ['CLEAR', 'VERIFIED', 'COMPLETED', 'PASSED', 'APPROVED'].some((s) => vStatus.includes(s));
            if (isDone) {
              setActiveCandidate({
                id: formattedId,
                name: candName,
                status: vStatus,
                isCompleted: true,
              });
              await executeReportDownload(formattedId, candName);
              return;
            } else {
              setActiveCandidate({
                id: formattedId,
                name: candName,
                status: vStatus || 'IN PROGRESS',
                isCompleted: false,
              });
              setDownloadStatus({
                type: 'error',
                message: `Verification for ${candName} (${formattedId}) is ${vStatus || 'IN PROGRESS'}. Report download is available only after verification is completed.`,
              });
              return;
            }
          }
        } catch (_) {}
      }

      setDownloadStatus({
        type: 'error',
        message: `No candidate found matching "${raw}". Please enter a valid Candidate ID or Name.`,
      });
    } catch (err) {
      console.error('Lookup error:', err);
      setDownloadStatus({
        type: 'error',
        message: 'Failed to lookup candidate. Please try again.',
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  // ── Header Download icon click handler ────────────────────────────────────
  const handleDownloadIconClick = async () => {
    if (downloadModalOpen) {
      setDownloadModalOpen(false);
      return;
    }

    // 1. If active candidate from chat is already verified/completed: download immediately
    if (activeCandidate?.id && activeCandidate.isCompleted) {
      setDownloadModalOpen(true);
      await executeReportDownload(activeCandidate.id, activeCandidate.name);
      return;
    }

    // 2. If active candidate is not completed: alert and show drawer
    if (activeCandidate?.id && activeCandidate.isCompleted === false) {
      setDownloadStatus({
        type: 'error',
        message: `Verification for candidate ${activeCandidate.name || activeCandidate.id} is ${activeCandidate.status || 'not completed'}. Download is only available for completed candidates.`,
      });
      setDownloadModalOpen(true);
      return;
    }

    // 3. If candidate ID exists but completion status unconfirmed: lookup & download
    if (activeCandidate?.id && activeCandidate.isCompleted === null) {
      setDownloadModalOpen(true);
      await handleDownloadSubmit(activeCandidate.id);
      return;
    }

    // 4. No candidate yet: open drawer so user can input ID or name
    setDownloadStatus(null);
    setDownloadModalOpen(true);
  };

  const bodyRef    = useRef(null);
  const widgetRef  = useRef(null);

  // ── Click outside to close chatbot ─────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // ── Restore previous conversation on open, or initialize greeting & capabilities ───
  useEffect(() => {
    if (!open) return;

    // Check 15-minute inactivity timeout
    if (!isSessionValid(email)) {
      if (localStorage.getItem(SESSION_KEY)) {
        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(SESSION_OWNER_KEY);
        localStorage.removeItem(SESSION_MESSAGES_KEY);
        localStorage.removeItem(SESSION_LAST_ACTIVE_KEY);
        setSessionId(null);
      }
      setMessages(getInitialMessages(role, username));
      return;
    }

    // Touch rolling activity timestamp because the user reopened the chat panel
    localStorage.setItem(SESSION_LAST_ACTIVE_KEY, Date.now().toString());
    const storedId = localStorage.getItem(SESSION_KEY);
    if (!sessionId && storedId) {
      setSessionId(storedId);
    }

    // If messages are already in memory / cached from localStorage, keep them!
    if (messages.length > 0) {
      return;
    }

    // Otherwise (e.g. fresh reload where localStorage messages weren't cached), fetch from server
    let cancelled = false;
    setRestoring(true);

    getSessionHistory(storedId)
      .then((data) => {
        if (cancelled) return;
        const hist = data.conversation_history || [];
        if (hist.length > 0) {
          setSessionId(data.session_id);
          const restored = [
            ...getInitialMessages(role, username),
            ...hist.map((m) => ({
              role: m.role,
              content: m.content,
            })),
          ];
          setMessages(restored);
          localStorage.setItem(SESSION_MESSAGES_KEY, JSON.stringify(restored));
        } else {
          setMessages(getInitialMessages(role, username));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          if (err?.response?.status === 404) {
            localStorage.removeItem(SESSION_KEY);
            localStorage.removeItem(SESSION_OWNER_KEY);
            localStorage.removeItem(SESSION_MESSAGES_KEY);
            localStorage.removeItem(SESSION_LAST_ACTIVE_KEY);
            setSessionId(null);
          }
          setMessages(getInitialMessages(role, username));
        }
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Scroll to bottom ──────────────────────────────────────────────────────
  const scrollToBottom = useCallback((smooth = true) => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo({
        top: bodyRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }, []);

  // ── Auto scroll behavior: stay at top on welcome, scroll down on interaction ──
  useEffect(() => {
    const hasUser = messages.some((m) => m.role === 'user');
    if (!hasUser) {
      if (bodyRef.current) {
        bodyRef.current.scrollTop = 0;
      }
      setShowScrollBtn(false);
      return;
    }
    scrollToBottom(true);
    setShowScrollBtn(false);
  }, [messages, loading, scrollToBottom]);

  // ── Track scroll position to show/hide scroll-to-bottom button ────────────
  const handleBodyScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(distanceFromBottom > 80);
  }, []);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleBodyScroll);
    return () => el.removeEventListener('scroll', handleBodyScroll);
  }, [handleBodyScroll, open]);

  // ── Send a message ─────────────────────────────────────────────────────────
  const handleSend = async (text) => {
    setError('');
    const now = Date.now().toString();
    const updatedWithUser = [...messages, { role: 'user', content: text }];
    setMessages(updatedWithUser);
    localStorage.setItem(SESSION_MESSAGES_KEY, JSON.stringify(updatedWithUser));
    localStorage.setItem(SESSION_LAST_ACTIVE_KEY, now);
    setLoading(true);
    setShowScrollBtn(false);
    setTimeout(() => scrollToBottom(true), 50);

    try {
      const res = await sendMessage(text, sessionId);
      setSessionId(res.session_id);
      localStorage.setItem(SESSION_KEY, res.session_id);
      localStorage.setItem(SESSION_OWNER_KEY, email);
      localStorage.setItem(SESSION_LAST_ACTIVE_KEY, Date.now().toString());
      const currentToken = localStorage.getItem('bgc_refresh_token') || localStorage.getItem('bgc_token');
      if (currentToken) {
        localStorage.setItem(SESSION_REFRESH_TOKEN_KEY, currentToken);
      }
      const updatedWithBot = [
        ...updatedWithUser,
        {
          role: 'assistant',
          content: res.response,
          guardrailTriggered: res.guardrail_triggered,
        },
      ];
      setMessages(updatedWithBot);
      localStorage.setItem(SESSION_MESSAGES_KEY, JSON.stringify(updatedWithBot));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 404) {
        setError('Your login session has expired. Starting a fresh session.');
        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(SESSION_OWNER_KEY);
        localStorage.removeItem(SESSION_MESSAGES_KEY);
        localStorage.removeItem(SESSION_LAST_ACTIVE_KEY);
        setSessionId(null);
      } else {
        setError(
          err?.response?.data?.detail ||
            'Could not reach the assistant. Please try again.'
        );
      }
    } finally {
      setLoading(false);
      setTimeout(() => scrollToBottom(true), 50);
    }
  };

  // ── Clear conversation ─────────────────────────────────────────────────────
  const handleClear = () => {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(SESSION_OWNER_KEY);
    localStorage.removeItem(SESSION_MESSAGES_KEY);
    localStorage.removeItem(SESSION_LAST_ACTIVE_KEY);
    localStorage.removeItem(SESSION_REFRESH_TOKEN_KEY);
    setSessionId(null);
    setError('');
    setActiveCandidate(null);
    setDownloadModalOpen(false);
    setDownloadStatus(null);
    setDownloadQuery('');
    setMessages(getInitialMessages(role, username));
  };

  // ── Launcher (always rendered; panel slides in/out) ───────────────────────
  return (
    <div ref={widgetRef} className="chatbot-widget-container">
      {/* Launcher bubble */}
      <button
        className={`chatbot-fab-launcher${open ? ' open-state' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Open TrueFace AI Chat"
        title="TrueFace AI"
      >
        <span className="fab-hover-text">
          <span className="brand-true">True</span>&nbsp;<span className="brand-face">Face</span>
        </span>
        <span className="fab-icon-slot">
          <img
            src={logo || `${process.env.PUBLIC_URL || ''}/Trueface.png`}
            alt=""
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `${process.env.PUBLIC_URL || ''}/Trueface.png`;
            }}
          />
        </span>
      </button>

      {/* ── Chat Panel ──────────────────────────────────────────────────────── */}
      <div className={`chatbot-panel${open ? ' chatbot-panel--open' : ''}`}>

        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div className="chatbot-header" style={{ backgroundColor: roleConfig.color }}>
          {/* Logo + title group */}
          <div className="bot-header-icon">
            <img
              src={logo || `${process.env.PUBLIC_URL || ''}/Trueface.png`}
              alt=""
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `${process.env.PUBLIC_URL || ''}/Trueface.png`;
              }}
            />
          </div>
          <div className="chatbot-header-left" style={{ flex: 1 }}>
            <span className="chatbot-header-title">True Face AI</span>
            <span className="bot-subtitle">
              <span className="bot-online-dot" />Online
            </span>
          </div>
          <div className="chatbot-header-actions">
            {isClient && (
              <button
                className={`chatbot-icon-btn chatbot-download-icon-btn${downloadModalOpen ? ' active' : ''}`}
                title={
                  activeCandidate?.id
                    ? `Download BGC Report: ${activeCandidate.name || activeCandidate.id}${activeCandidate.isCompleted ? ' (Completed)' : ''}`
                    : 'Download Candidate BGC Report'
                }
                onClick={handleDownloadIconClick}
                aria-label="Download candidate report"
              >
                <Download size={14} />
              </button>
            )}
            <button className="chatbot-icon-btn" title="Clear chat" onClick={handleClear} aria-label="Clear chat">
              <Eraser size={14} />
            </button>
            <button className="chatbot-icon-btn" title="Close" onClick={() => setOpen(false)} aria-label="Close chat">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* ── Client Report Download Drawer (Client Module only) ────────────── */}
        {isClient && downloadModalOpen && (
          <div className="chatbot-download-drawer">
            <div className="chatbot-download-drawer-header">
              <div className="chatbot-download-drawer-title">
                <Download size={13} />
                <span>Download Completed BGC Report</span>
              </div>
              <button
                className="chatbot-download-drawer-close"
                onClick={() => {
                  setDownloadModalOpen(false);
                  setDownloadStatus(null);
                }}
                title="Close download panel"
                aria-label="Close download panel"
              >
                <X size={13} />
              </button>
            </div>

            {activeCandidate?.id && (
              <div className="chatbot-download-active-card">
                <div className="active-card-meta">
                  <div className="active-card-name">
                    <strong>{activeCandidate.name || activeCandidate.id}</strong>
                    {activeCandidate.name && <span className="active-card-id"> ({activeCandidate.id})</span>}
                  </div>
                  <span className={`active-card-pill ${activeCandidate.isCompleted ? 'pill-completed' : 'pill-wip'}`}>
                    {activeCandidate.status || (activeCandidate.isCompleted ? 'COMPLETED' : 'IN PROGRESS')}
                  </span>
                </div>
                {activeCandidate.isCompleted && (
                  <button
                    className="chatbot-download-quick-btn"
                    onClick={() => executeReportDownload(activeCandidate.id, activeCandidate.name)}
                    disabled={downloadLoading}
                    title="Download this candidate's PDF report"
                  >
                    <Download size={12} />
                    <span>Download PDF</span>
                  </button>
                )}
              </div>
            )}

            <div className="chatbot-download-form">
              <div className="chatbot-download-form-label">
                {activeCandidate?.id ? 'Or download for another candidate:' : 'Enter Candidate ID or Name:'}
              </div>
              <div className="chatbot-download-input-row">
                <input
                  type="text"
                  className="chatbot-download-input"
                  placeholder="e.g. TRU000014 or Shaik Maktumbi"
                  value={downloadQuery}
                  onChange={(e) => setDownloadQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleDownloadSubmit(downloadQuery);
                    }
                  }}
                  disabled={downloadLoading}
                  aria-label="Candidate ID or Name input"
                />
                <button
                  className="chatbot-download-submit-btn"
                  onClick={() => handleDownloadSubmit(downloadQuery)}
                  disabled={downloadLoading || !downloadQuery.trim()}
                  aria-label="Download report"
                >
                  {downloadLoading ? 'Downloading...' : 'Download'}
                </button>
              </div>
            </div>

            {downloadStatus && (
              <div className={`chatbot-download-status-banner status-${downloadStatus.type}`}>
                {downloadStatus.type === 'success' && <Check size={14} className="status-icon" />}
                {downloadStatus.type === 'error' && <AlertCircle size={14} className="status-icon" />}
                <span className="status-text">{downloadStatus.message}</span>
              </div>
            )}
          </div>
        )}




      {/* ── Body wrapper (relative for scroll btn positioning) ─────────────── */}
      <div className="chatbot-body-wrapper">
        <div ref={bodyRef} className="chatbot-body">

          {restoring ? (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
              <ScanLineLoader />
            </div>
          ) : (
            <>
              {messages.map((m, i) => (
                <MessageBubble
                  key={i}
                  role={m.role}
                  content={m.content}
                  isWelcome={m.isWelcome || (i === 0 && m.role === 'assistant' && !messages.some((msg) => msg.role === 'user'))}
                  guardrailTriggered={m.guardrailTriggered}
                  onSend={handleSend}
                />
              ))}
              {!messages.some((m) => m.role === 'user') && (
                <div style={{ marginTop: 10, marginBottom: 8, paddingLeft: 42, paddingRight: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                    Suggested Inquiries
                  </div>
                  <CapabilityChips
                    examples={roleConfig.examples}
                    roleColor={roleConfig.color}
                    onPick={handleSend}
                  />
                </div>
              )}
            </>
          )}

          {loading && !restoring && (
            <div style={{ paddingLeft: 36 }}>
              <ScanLineLoader />
            </div>
          )}

          {error && (
            <div className="chatbot-error">{error}</div>
          )}
        </div>

        {/* ── Scroll-to-bottom floating button (ChatGPT style) ───────────── */}
        <button
          className={`chatbot-scroll-btn${showScrollBtn ? ' chatbot-scroll-btn--visible' : ''}`}
          onClick={scrollToBottom}
          aria-label="Scroll to latest message"
          title="Jump to latest"
        >
          <ArrowDown size={16} strokeWidth={2} />
        </button>
      </div>

        {/* ── Input ────────────────────────────────────────────────────────── */}
        <ChatInput onSend={handleSend} disabled={loading || restoring} />
      </div>
    </div>
  );
};

export default FloatingChatWidget;
